/**
 * Activity Type Icons
 * Using Lucide icons for consistent, professional design
 * 
 * Design System Colors:
 * - Cardio: #42A5A5 (cedar-accent-sky)
 * - Strength: #7834b5 (purple)
 * - HIIT: #A6496E (mauve/berry)
 * - Yoga: #5FA287 (cedar light)
 * - Mobility: #1F6C4F (cedar-primary)
 * - Rest: rgba(17, 60, 43, 0.72) (coloron-surface600)
 */

import React from 'react';
import { Activity, Dumbbell, Zap, Heart, Move, TreePalm } from 'lucide-react';
import { BaseIconProps } from './BaseIcon';

/**
 * Cardio Icon - Activity icon from Lucide
 * Color: rgba(201, 45, 32, 1) (rehab text color - red)
 * 🔧 FIX JAN 22, 2026: Changed from teal to red
 */
export const CardioIcon: React.FC<BaseIconProps> = ({
  size = 20,
  color = 'rgba(201, 45, 32, 1)',
  className = '',
  'aria-label': ariaLabel = 'Cardio activity',
  ...props
}) => {
  return (
    <Activity
      size={size}
      color={color}
      className={className}
      aria-label={ariaLabel}
      {...props}
    />
  );
};

/**
 * Strength Icon - Dumbbell icon from Lucide
 * Color: #7834b5 (yoga text color - purple)
 */
export const StrengthIcon: React.FC<BaseIconProps> = ({
  size = 20,
  color = '#7834b5',
  className = '',
  'aria-label': ariaLabel = 'Strength training',
  ...props
}) => {
  return (
    <Dumbbell
      size={size}
      color={color}
      className={className}
      aria-label={ariaLabel}
      {...props}
    />
  );
};

/**
 * HIIT Icon - Zap icon from Lucide (lightning bolt)
 * Color: #A6496E (mauve/berry)
 */
export const HIITIcon: React.FC<BaseIconProps> = ({
  size = 20,
  color = '#A6496E',
  className = '',
  'aria-label': ariaLabel = 'HIIT training',
  ...props
}) => {
  return (
    <Zap
      size={size}
      color={color}
      className={className}
      aria-label={ariaLabel}
      {...props}
    />
  );
};

/**
 * Yoga Icon - Heart icon from Lucide (mind-body connection)
 * Color: #5FA287 (cedar light)
 */
export const YogaIcon: React.FC<BaseIconProps> = ({
  size = 20,
  color = '#5FA287',
  className = '',
  'aria-label': ariaLabel = 'Yoga practice',
  ...props
}) => {
  return (
    <Heart
      size={size}
      color={color}
      className={className}
      aria-label={ariaLabel}
      {...props}
    />
  );
};

/**
 * Mobility Icon - Stretching person SVG from activity-icons
 * Color: #1F6C4F (cedar-primary)
 * 🔧 FIX JAN 22, 2026: Changed from Move (compass) to stretching person SVG
 */
export const MobilityIcon: React.FC<BaseIconProps> = ({
  size = 20,
  color = '#1F6C4F',
  className = '',
  'aria-label': ariaLabel = 'Mobility work',
  ...props
}) => {
  return (
    <img 
      src="/activity-icons/stretching.svg" 
      alt={ariaLabel}
      width={size}
      height={size}
      className={className}
      style={{ filter: `brightness(0) saturate(100%) invert(37%) sepia(18%) saturate(1447%) hue-rotate(118deg) brightness(94%) contrast(92%)` }}
      {...props}
    />
  );
};

/**
 * Rest Icon - Palm tree icon from Lucide (relaxation/recovery indicator)
 * Color: #42A5A5 (cedar-accent-sky/teal)
 * 🔧 FIX JAN 25, 2026: Changed from Plus to TreePalm icon per user request
 */
export const RestIcon: React.FC<BaseIconProps> = ({
  size = 20,
  color = '#42A5A5',
  className = '',
  'aria-label': ariaLabel = 'Rest day',
  ...props
}) => {
  return (
    <TreePalm
      size={size}
      color={color}
      className={className}
      aria-label={ariaLabel}
      {...props}
    />
  );
};

// Export all activity icons
export const ActivityIcons = {
  Cardio: CardioIcon,
  Strength: StrengthIcon,
  HIIT: HIITIcon,
  Yoga: YogaIcon,
  Mobility: MobilityIcon,
  Rest: RestIcon,
};

